"use client";

import React, { createContext, useState, useContext, useEffect } from "react";
import { toast } from "react-toastify";
import { BASE_URL } from "../config/apiConfig";

export const CertificateContext = createContext();

// CHANGED: Added a safe JSON parsing helper to prevent "Unexpected token '<'" crashes.
// This guards against cases where the backend returns an HTML error page (404/500/proxy errors)
// or an empty body instead of valid JSON, which previously caused res.json() to throw.
const safeParseJSON = async (res) => {
  try {
    const text = await res.text();
    if (!text) {
      return { status: res.status, message: "Empty response from server" };
    }
    try {
      return JSON.parse(text);
    } catch (parseErr) {
      return { status: res.status, message: "Invalid response format from server" };
    }
  } catch (readErr) {
    return { status: res.status || 500, message: "Failed to read server response" };
  }
};

export const CertificateProvider = ({ children }) => {
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Get Token
  const getToken = () => localStorage.getItem("token");

  // Fetch All Certificates — fetch operation, no toasts
  // CHANGED: Removed toast.error calls — this is a GET/fetch operation, not a user action.
  const fetchCertificates = async () => {
    setLoading(true);
    setError("");

    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/certificates`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await safeParseJSON(res); // CHANGED: safe parsing instead of res.json()

      if (data.status === 200) {
        setCertificates(data.data);
      } else {
        setError(data.message || "Failed to fetch certificates");
        // CHANGED: removed toast.error(data.message || "Failed to fetch certificates")
      }
    } catch (err) {
      console.error(err);
      setError("Error fetching certificates");
      // CHANGED: removed toast.error("Error fetching certificates")
    } finally {
      setLoading(false);
    }
  };

  // Create Certificate — user action, toasts preserved
  const createCertificate = async (formData) => {
    setLoading(true);
    setError("");

    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/certificates`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await safeParseJSON(res); // CHANGED: safe parsing instead of res.json()

      if (data.status === 201) {
        await fetchCertificates();
        toast.success(data.message || "Certificate created successfully"); // KEPT: user-initiated action
        return {
          success: true,
          message: data.message,
        };
      }

      toast.error(data.message || "Failed to create certificate"); // KEPT: user-initiated action
      return {
        success: false,
        message: data.message,
      };
    } catch (err) {
      console.error(err);
      toast.error("Error creating certificate"); // KEPT: user-initiated action

      return {
        success: false,
        message: "Error creating certificate",
      };
    } finally {
      setLoading(false);
    }
  };

  // Update Certificate — user action, toasts preserved
  const updateCertificate = async (id, formData) => {
    setLoading(true);

    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/certificates/${id}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await safeParseJSON(res); // CHANGED: safe parsing instead of res.json()

      if (data.status === 200) {
        await fetchCertificates();
        toast.success(data.message || "Certificate updated successfully"); // KEPT: user-initiated action

        return {
          success: true,
          message: data.message,
        };
      }

      toast.error(data.message || "Failed to update certificate"); // KEPT: user-initiated action
      return {
        success: false,
        message: data.message,
      };
    } catch (err) {
      console.error(err);
      toast.error("Error updating certificate"); // KEPT: user-initiated action

      return {
        success: false,
        message: "Error updating certificate",
      };
    } finally {
      setLoading(false);
    }
  };

  // Update Status — user action, toasts preserved
  const updateCertificateStatus = async (
    id,
    status,
    update_audit_id
  ) => {
    try {
      const token = getToken();

      const res = await fetch(
        `${BASE_URL}/certificates/${id}/status`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            status,
            update_audit_id,
          }),
        }
      );

      const data = await safeParseJSON(res); // CHANGED: safe parsing instead of res.json()

      if (data.status === 200) {
        await fetchCertificates();
        toast.success(data.message || "Certificate status updated successfully"); // KEPT: user-initiated action

        return {
          success: true,
          message: data.message,
        };
      }

      toast.error(data.message || "Failed to update certificate status"); // KEPT: user-initiated action
      return {
        success: false,
        message: data.message,
      };
    } catch (err) {
      console.error(err);
      toast.error("Error updating certificate status"); // KEPT: user-initiated action

      return {
        success: false,
        message: "Error updating certificate status",
      };
    }
  };

  // Delete Certificate — user action, toasts preserved
  const deleteCertificate = async (id, delete_audit_id) => {
    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/certificates/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          delete_audit_id,
        }),
      });

      const data = await safeParseJSON(res); // CHANGED: safe parsing instead of res.json()

      if (data.status === 200) {
        await fetchCertificates();
        toast.success(data.message || "Certificate deleted successfully"); // KEPT: user-initiated action

        return {
          success: true,
          message: data.message,
        };
      }

      toast.error(data.message || "Failed to delete certificate"); // KEPT: user-initiated action
      return {
        success: false,
        message: data.message,
      };
    } catch (err) {
      console.error(err);
      toast.error("Error deleting certificate"); // KEPT: user-initiated action

      return {
        success: false,
        message: "Error deleting certificate",
      };
    }
  };

  useEffect(() => {
    const token = getToken();

    if (token) {
      fetchCertificates();
    }
  }, []);

  return (
    <CertificateContext.Provider
      value={{
        certificates,
        loading,
        error,
        fetchCertificates,
        createCertificate,
        updateCertificate,
        updateCertificateStatus,
        deleteCertificate,
      }}
    >
      {children}
    </CertificateContext.Provider>
  );
};

export const useCertificates = () => useContext(CertificateContext);